import { useEffect, useState } from 'react';
import CountUp from 'react-countup';
import {
    ResponsiveContainer,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    PieChart, Pie, Cell, Legend,
} from 'recharts';
import api from '../../services/api';

// Paleta de cores para tipos dinâmicos
const PALETTE = ['#85287e', '#D55B15', '#2563eb', '#16a34a', '#9333ea', '#dc2626', '#0891b2', '#ca8a04'];

// ── Componente KPI card ───────────────────────────────────────
function KpiCard({ label, value, icon, color, loading }) {
    return (
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-5 tw-flex tw-items-center tw-gap-4 tw-min-w-0">
            {/* <div
        className="tw-flex-shrink-0 tw-w-12 tw-h-12 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-text-2xl"
        style={{ background: `${color}18` }}
      >
        {icon}
      </div> */}
            <div className="tw-min-w-0">
                <p className="tw-text-xs tw-text-gray-500 tw-uppercase tw-tracking-wide tw-font-semibold tw-truncate">
                    {label}
                </p>
                <p className="tw-text-2xl tw-font-extrabold tw-leading-tight" style={{ color }}>
                    {loading ? (
                        <span className="tw-text-gray-300 tw-animate-pulse">···</span>
                    ) : (
                        <CountUp end={value} duration={1.5} separator="." />
                    )}
                </p>
            </div>
        </div>
    );
}

// ── Tooltip customizado para barras ──────────────────────────
function CustomTooltipBar({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-px-3 tw-py-2 tw-text-sm">
            <p className="tw-font-bold tw-text-gray-700 tw-mb-1">{label}</p>
            {payload.map((p, i) => (
                <p key={i} style={{ color: p.fill }}>
                    {p.name}: <strong>{p.value}</strong>
                </p>
            ))}
        </div>
    );
}

// ── Tooltip customizado para donut ───────────────────────────
function CustomTooltipPie({ active, payload }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-px-3 tw-py-2 tw-text-sm">
            <p style={{ color: payload[0].payload.fill }} className="tw-font-bold">
                {payload[0].name}
            </p>
            <p className="tw-text-gray-700">
                <strong>{payload[0].value}</strong> ({((payload[0].value / payload[0].payload.total) * 100).toFixed(1)}%)
            </p>
        </div>
    );
}

// ── Componente principal ─────────────────────────────────────
export default function DashboardStats() {
    const [loading, setLoading] = useState(true);
    const [kpi, setKpi] = useState({ total: 0, pendentes: 0, renovacoes: 0, inspeccoes: 0, reclamacoes: 0, reprovados: 0 });
    const [provinciasData, setProvinciasData] = useState([]);
    const [provinciasView, setProvinciasView] = useState('top10');
    const [tiposData, setTiposData] = useState([]);
    const [tiposCounts, setTiposCounts] = useState([]);

    const provinciasChartData = provinciasView === 'geral'
        ? provinciasData
        : provinciasData.slice(0, 10);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                // Buscar tipos + total + pendentes em paralelo
                const [tiposRes, totalRes, menuRes, provinciasRes] = await Promise.all([
                    api.get('/tipo-estabelecimentos'),
                    api.get('/estabelecimentos?count=true'),
                    api.get('/menu/dlif').catch(() => ({ data: {} })),
                    api.get('/provincias').catch(() => ({ data: {} })),
                ]);

                const tipos = tiposRes.data.tipoEstabelecimentos ?? [];
                const total = totalRes.data.totalItems ?? 0;
                const menu = menuRes.data ?? {};
                // const reclamacoes = reclamacoesRes.data?.totalItems ?? 0;

                // KPI – conta total por tipo
                const counts = await Promise.all(
                    tipos.map(t =>
                        api.get(`/estabelecimentos?tipoId=${t._id}&count=true`)
                            .then(r => ({ ...t, count: r.data.totalItems ?? 0, nome: t.designacao ?? t.nome ?? t.name ?? t.tipo ?? t._id }))
                            .catch(() => ({ ...t, count: 0, nome: t.designacao ?? t.nome ?? t.name ?? t.tipo ?? t._id }))
                    )
                );

                setTiposCounts(counts);

                setKpi({
                    total,
                    // contagem de processos em andamento: soma de todos os estados pendentes do menu
                    pendentes: (menu.estabelecimentosAnalise ?? 0) + (menu.chefeSeccao ?? 0) + (menu.chefeDepartamento ?? 0) + (menu.directoraGeralSE ?? 0) + (menu.analiseInspeccoes ?? 0) + (menu.directorGeral ?? 0),
                    renovacoes: menu.solicitacoesRenovacao ?? 0,
                    inspeccoes: menu.inspeccoes ?? 0,
                    // reclamacoes,
                    reprovados: menu.estabelecimentosReprovados ?? 0,
                });

                // tipos com cor dinâmica
                setTiposData(
                    counts.map((t, i) => ({
                        name: t.designacao ?? t.nome ?? t.name ?? `Tipo ${i + 1}`,
                        value: t.count,
                        fill: PALETTE[i % PALETTE.length],
                        total,
                    })).filter(t => t.value > 0)
                );

                // Barras – top províncias
                const provinciasApi = Array.isArray(provinciasRes?.data?.provincias)
                    ? provinciasRes.data.provincias
                    : [];
                const TOP = provinciasApi
                    .map((p) => p?.designacao)
                    .filter(Boolean);

                const pResults = await Promise.all(
                    TOP.map(p =>
                        api.get(`/estabelecimentos?provincia=${p}&count=true`)
                            .then(r => ({ provincia: p, total: r.data.totalItems ?? 0 }))
                            .catch(() => ({ provincia: p, total: 0 }))
                    )
                );
                setProvinciasData(
                    pResults
                        .filter((p) => p.total > 0)
                        .sort((a, b) => b.total - a.total)
                );

            } catch (e) {
                console.error('Dashboard stats error:', e);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, []);

    return (
        <div className="tw-space-y-5">

            {/* ── Linha 1: Totais gerais ─────────────────────── */}
            <p className="tw-text-[11px] tw-text-gray-400 tw-uppercase tw-tracking-widest tw-font-semibold tw-mb-2 tw-px-0.5 tw--mb-3">
                {/* Por tipo de estabelecimento */}
                Indicadores por fazes relevantes
            </p>
            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-3">
                {/* Autorizados Estabelecimentos*/}
                <KpiCard label="Total Autorizados" value={kpi.total} icon="🏪" color="#85287e" loading={loading} />
                <KpiCard label="Em Andamento" value={kpi.pendentes} icon="📋" color="#f59e0b" loading={loading} />
                <KpiCard label="Inspecções" value={kpi.inspeccoes} icon="🔍" color="#0891b2" loading={loading} />
                <KpiCard label="Reprovados" value={kpi.reprovados} icon="❌" color="#6b7280" loading={loading} />
                <KpiCard label="Renovações" value={kpi.renovacoes} icon="🔄" color="#2563eb" loading={loading} />
                {/* <KpiCard label="Reclamações" value={kpi.reclamacoes} icon="📨" color="#dc2626" loading={loading} /> */}
            </div>

            {/* ── Linha 2: KPI por tipo de estabelecimento ──── */}
            {tiposCounts.length > 0 && (
                <div>
                    <p className="tw-text-[11px] tw-text-gray-400 tw-uppercase tw-tracking-widest tw-font-semibold tw-mb-2 tw-px-0.5">
                        Por tipo de estabelecimento
                    </p>
                    <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-4 xl:tw-grid-cols-6 tw-gap-3">
                        {tiposCounts.map((t, i) => (
                            <KpiCard
                                key={t._id}
                                label={t.nome ?? `Tipo ${i + 1}`}
                                value={t.count}
                                icon={PALETTE[i % PALETTE.length] ? '🏷️' : '🏷️'}
                                color={PALETTE[i % PALETTE.length]}
                                loading={loading}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* ── Gráficos ──────────────────────────────────── */}
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">

                {/* Barras – estabelecimentos por província */}
                <div className="tw-col-span-2 tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-5">
                    <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-3 tw-mb-4">
                        <p className="tw-text-sm tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wide">
                            Estabelecimentos por Províncias
                        </p>
                        <div className="tw-inline-flex tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setProvinciasView('top10')}
                                className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-colors ${provinciasView === 'top10'
                                    ? 'tw-bg-[#85287e] tw-text-white'
                                    : 'tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50'
                                    }`}
                            >
                                TOP 10
                            </button>
                            <button
                                type="button"
                                onClick={() => setProvinciasView('geral')}
                                className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-colors ${provinciasView === 'geral'
                                    ? 'tw-bg-[#85287e] tw-text-white'
                                    : 'tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50'
                                    }`}
                            >
                                Geral
                            </button>
                        </div>
                    </div>
                    {loading ? (
                        <div className="tw-h-52 tw-flex tw-items-center tw-justify-center tw-text-gray-300 tw-text-sm">
                            A carregar...
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={345}>
                            <BarChart data={provinciasChartData} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="provincia"
                                    tick={{ fontSize: 11, fill: '#6b7280' }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: '#6b7280' }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip content={<CustomTooltipBar />} />
                                <Bar dataKey="total" name="Total" fill="#85287e" radius={[6, 6, 0, 0]} maxBarSize={40} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>

                {/* tipos de estabelecimento */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-5">
                    <p className="tw-text-sm tw-font-bold tw-text-gray-700 tw-mb-4 tw-uppercase tw-tracking-wide">
                        Tipos de Estabelecimento
                    </p>
                    {loading ? (
                        <div className="tw-h-52 tw-flex tw-items-center tw-justify-center tw-text-gray-300 tw-text-sm">
                            A carregar...
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={345}>
                            <PieChart>
                                <Pie
                                    data={tiposData}
                                    cx="50%"
                                    cy="45%"
                                    innerRadius={55}
                                    outerRadius={80}
                                    paddingAngle={3}
                                    dataKey="value"
                                >
                                    {tiposData.map((entry, i) => (
                                        <Cell key={i} fill={entry.fill} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltipPie />} />
                                <Legend
                                    formatter={(v, e) => (
                                        <span style={{ fontSize: 12, color: '#374151' }}>
                                            {v} <strong style={{ color: e.color }}>{e.payload.value}</strong>
                                        </span>
                                    )}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
        </div>
    );
}
